import {
 mergeWith, cloneDeep, get, set,
} from 'lodash';

import { ROOT_NODE_TYPE } from '../constants';
import { removeNodeRecursively, updatePhases } from './helper';
import { ACTION_TYPES } from './types';

const defaultState = {
  initial: {
    nodeList: [],
    externalSources: [],
    hierarchy: {},
  },
  nodeList: [],
  externalSources: [],
  hierarchy: {},
  isNewPlaza: false,
  plazaId: null,
  isFetching: false,
  dynamicData: {},
  editableNodeId: null,
  requestQueue: {},
  settings: {},
  plazaErrors: null,
  nodeErrors: [],
  nodeTopologyErrors: [],
  lockInterval: null,
  lockTimeout: null,
  confirmationTimeout: null,
  editable: true,
  lockUserName: null,
  lastUpdated: null,
  isPatching: false,
  byAddress: {},
};

const mergeNodeProps = (node, props) => {
  const normalizedProps = {
    ...props,
    staticSection: {
      ...props.staticSection,
      positionX: props.staticSection.positionX,
      positionY: props.staticSection.positionY,
    },
  };

  mergeWith(node, normalizedProps, (obj, source) => {
    if (source && (source.relativeRotation || source.schedules)) {
      return source;
    }
  });
};

function plazaTopology(state = defaultState, action) {
  switch (action.type) {
    case ACTION_TYPES.INIT_EDITOR:
      return {
        ...state,
        nodeList: cloneDeep(action.list),
        hierarchy: cloneDeep(action.hierarchy),
        externalSources: cloneDeep(action.externalSources),
        initial: {
          nodeList: cloneDeep(action.list),
          hierarchy: cloneDeep(action.hierarchy),
          externalSources: cloneDeep(action.externalSources),
        },
      };

    case ACTION_TYPES.RESET_TO_INITIAL_STATE:
      return {
        ...state,
        nodeList: cloneDeep(state.initial.nodeList),
        hierarchy: cloneDeep(state.initial.hierarchy),
        externalSources: cloneDeep(state.initial.externalSources),
        byAddress: {},
      };

    case ACTION_TYPES.SET_NODE_LIST:
      return { ...state, nodeList: action.payload.list };

    case ACTION_TYPES.SET_EXTERNAL_SOURCES:
      return { ...state, externalSources: action.payload.sources };

    case ACTION_TYPES.SET_REGULAR_AND_EXTERNAL_SOURCES:
      return { ...state, externalSources: action.payload.sources, nodeList: action.payload.regular };

    case ACTION_TYPES.NEW_PLAZA:
      return { ...state, isNewPlaza: action.isNew };

    case ACTION_TYPES.SET_CHARGERS_WITHOUT_PLAZA:
      return { ...state, chargersWithoutPlaza: action.chargers };

    case ACTION_TYPES.SET_PLAZA_ID:
      return { ...state, plazaId: action.plazaId };

    case ACTION_TYPES.SET_PLAZA_STATUS:
      return { ...state, plazaStatus: action.plazaStatus };

    case ACTION_TYPES.TOGGLE_FETCHING:
      return { ...state, isFetching: action.isFetching, error: action.error };

    case ACTION_TYPES.UPDATE_DYNAMIC_DATA:
      return { ...state, dynamicData: action.data };

    case ACTION_TYPES.UPDATE_TOPOLOGY: {
      const { nodeList } = state;
      const { hierarchy } = action.payload;

      updatePhases(hierarchy, nodeList);

      return { ...state, hierarchy };
    }

    case ACTION_TYPES.UPDATE_NODES_LIST: {
      const { nodeList } = action.payload;

      return { ...state, nodeList };
    }

    case ACTION_TYPES.SET_EDITABLE_NODE_ID: {
      return { ...state, editableNodeId: action.payload };
    }

    case ACTION_TYPES.CREATE_NODE: {
      let hierarchy = state.hierarchy;
      const { plazaId } = state;
      const node = action.payload.node;

      if (node.topologyNodeType === ROOT_NODE_TYPE) {
        hierarchy = { id: plazaId, children: [] };
        node.id = plazaId;
      }

      return {
        ...state,
        hierarchy,
        nodeList: [...state.nodeList, node],
      };
    }

    case ACTION_TYPES.CREATE_RESOURCE_NODE: {
      const node = action.payload.node;
      const { externalSources } = state;

      return {
        ...state,
        externalSources: [...externalSources, node],
      };
    }

    case ACTION_TYPES.UPDATE_NODE: {
      const { nodeList, hierarchy } = state;
      const { payload: { id, props } } = action;
      const node = nodeList.find((node) => node.id === id);

      const oldMaxPower = get(node, 'staticSection.maxPower');
      const newMaxPower = get(props, 'staticSection.maxPower');
      const newMaxPowerMultiply = Number.parseFloat(newMaxPower) * 1000;

      if (newMaxPower && newMaxPower !== oldMaxPower) {
        props.staticSection.maxPower = newMaxPowerMultiply;
      }

      mergeNodeProps(node, props);
      updatePhases(hierarchy, nodeList);

      return { ...state, nodeList: [...state.nodeList] };
    }

    case ACTION_TYPES.UPDATE_RESOURCE_NODE: {
      const { externalSources } = state;
      const { payload: { id, props } } = action;
      const node = externalSources.find((node) => node.id === id);

      mergeNodeProps(node, props);

      return { ...state, externalSources: [...state.externalSources] };
    }

    case ACTION_TYPES.DELETE_NODE: {
      const { nodeList, hierarchy, externalSources } = state;
      const { payload: { nodeId } } = action;

      removeNodeRecursively(hierarchy, nodeId);

      // check if the deleted node is found as junction in any of external sources
      const externalNodesWithJunction = externalSources
        .filter((node) => get(node, 'staticSection.junctionNodeId') === nodeId);

      if (externalNodesWithJunction && externalNodesWithJunction.length > 0) {
        externalNodesWithJunction
          .forEach((externalNode) =>
            set(externalNode, 'staticSection.junctionNodeId', null));
      }
      const updatedList = nodeList.filter((node) => node.id !== nodeId);

      return {
        ...state,
        nodeList: updatedList,
        hierarchy,
        externalSources: [...state.externalSources],
      };
    }

    case ACTION_TYPES.DELETE_RESOURCE_NODE: {
      const { externalSources } = state;
      const { payload: { nodeId } } = action;

      return {
        ...state,
        externalSources: externalSources.filter((node) => node.id !== nodeId),
      };
    }

    case ACTION_TYPES.ADD_TO_REQUEST_QUEUE: {
      const { payload: { requestId, requestType, previousState } } = action;

      return {
        ...state,
        requestQueue: {
          ...state.requestQueue,
          [requestId]: { type: requestType, state: previousState },
        },
      };
    }

    case ACTION_TYPES.REMOVE_FROM_REQUEST_QUEUE: {
      const { payload: { requestId } } = action;

      const filteredQueue = Object.keys(state.requestQueue).reduce((acc, id) => {
          if (id !== requestId) acc[id] = state.requestQueue[id];

          return acc;
        }, {});

      return { ...state, requestQueue: filteredQueue };
    }

    case ACTION_TYPES.SET_PLAZA_SETTINGS:
      return { ...state, settings: action.payload };

    case ACTION_TYPES.SET_PLAZA_ERRORS:
      return { ...state, plazaErrors: action.payload };

    case ACTION_TYPES.SET_NODE_ERRORS:
      return { ...state, nodeErrors: action.payload };

    case ACTION_TYPES.SET_NODE_TOPOLOGY_ERRORS:
      return { ...state, nodeTopologyErrors: action.payload };

    case ACTION_TYPES.SET_LOCK_INTERVAL:
      return { ...state, lockInterval: action.payload };

    case ACTION_TYPES.CLEAR_LOCK_INTERVAL:
      clearInterval(state.lockInterval);

      return { ...state, lockInterval: null };

    case ACTION_TYPES.SET_LOCK_TIMEOUT:
      return { ...state, lockTimeout: action.payload };

    case ACTION_TYPES.CLEAR_LOCK_TIMEOUT:
      clearTimeout(state.lockTimeout);

      return { ...state, lockTimeout: null };

    case ACTION_TYPES.SET_CONFIRMATION_TIMEOUT:
      return { ...state, confirmationTimeout: action.payload };

    case ACTION_TYPES.CLEAR_CONFIRMATION_TIMEOUT:
      clearTimeout(state.confirmationTimeout);

      return { ...state, confirmationTimeout: null };

    case ACTION_TYPES.SET_EDITOR_EDITABLE:
      return {
        ...state,
        editable: action.payload,
      };

    case ACTION_TYPES.UPDATE_LOCK_STATUS:
      return {
        ...state,
        editable: action.editable,
        lockUserName: action.username,
      };

    case ACTION_TYPES.SET_UPDATED_STATUS:
      return {
        ...state,
        lastUpdated: action.payload,
      };

    case ACTION_TYPES.SET_PLAZA_PATCHING:
      return {
        ...state,
        isPatching: action.payload,
      };

    case ACTION_TYPES.SET_CHARGERS_BY_ADDRESS: {
      const nodeList = Object.keys(action.payload.nodes).map((id) => action.payload.nodes[id]);

      return { ...state, byAddress: { nodeList, hierarchy: action.payload.hierarchy } };
    }

    case ACTION_TYPES.RESET_CHARGERS_BY_ADDRESS: {
      return { ...state, byAddress: {} };
    }

    default:
      return state;
  }
}

export default plazaTopology;
